import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Page,
  Card,
  TextField,
  Button,
  BlockStack,
  InlineStack,
  Text,
  Box,
  EmptyState,
  Icon,
  Select,
  Popover,
  ActionList,
} from "@shopify/polaris";
import { SearchIcon, DeleteIcon, SortIcon, ChevronDownIcon } from "@shopify/polaris-icons";
import { apiCall } from "../../helper/Common";
import PageLoader from "../../components/PageLoader";
import { useAppTitleBar } from "../../hooks/useAppTitleBar";

const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 400;

const SORT_BY_OPTIONS = [
  { label: "Signed up date", value: "date", shortLabel: "Signed up" },
  { label: "Name", value: "name", shortLabel: "Name" },
  { label: "Email", value: "email", shortLabel: "Email" },
  { label: "Guests", value: "guests", shortLabel: "Guests" },
];

const SORT_DIR_BY_FIELD = {
  date: [
    { label: "Newest first", value: "desc" },
    { label: "Oldest first", value: "asc" },
  ],
  name: [
    { label: "A → Z", value: "asc" },
    { label: "Z → A", value: "desc" },
  ],
  email: [
    { label: "A → Z", value: "asc" },
    { label: "Z → A", value: "desc" },
  ],
  guests: [
    { label: "Low to high", value: "asc" },
    { label: "High to low", value: "desc" },
  ],
};

function sortByShortLabel(sortBy) {
  const hit = SORT_BY_OPTIONS.find((o) => o.value === sortBy);
  return hit?.shortLabel || hit?.label || "Signed up";
}

function SortByControl({ sortBy, sortDir, sortDirOptions, onSortByChange, onSortDirChange, inline }) {
  const [open, setOpen] = useState(false);
  const selectedLabel = sortByShortLabel(sortBy);

  const fieldItems = SORT_BY_OPTIONS.map((opt) => ({
    content: opt.label,
    active: sortBy === opt.value,
    onAction: () => onSortByChange(opt.value),
  }));

  const orderItems = sortDirOptions.map((opt) => ({
    content: opt.label,
    active: sortDir === opt.value,
    onAction: () => {
      onSortDirChange(opt.value);
      setOpen(false);
    },
  }));

  return (
    <Popover
      active={open}
      onClose={() => setOpen(false)}
      preferredAlignment="right"
      activator={
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-haspopup="listbox"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            width: inline ? "auto" : "100%",
            minHeight: inline ? 36 : undefined,
            padding: inline ? "6px 10px" : "10px 12px",
            margin: 0,
            border: inline ? "1px solid var(--p-color-border, #e3e3e3)" : "none",
            borderBottom: inline ? undefined : "1px solid var(--p-color-border, #e3e3e3)",
            borderRadius: inline ? "var(--p-border-radius-200, 8px)" : 0,
            background: "var(--p-color-bg-surface, #fff)",
            cursor: "pointer",
            textAlign: "left",
            flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          <InlineStack gap="200" blockAlign="center">
            <Icon source={SortIcon} tone="subdued" />
            <Text as="span" variant="bodyMd">
              Sort by
            </Text>
          </InlineStack>
          <InlineStack gap="100" blockAlign="center">
            <Text as="span" variant="bodyMd" fontWeight="medium">
              {selectedLabel}
            </Text>
            <Icon source={ChevronDownIcon} tone="subdued" />
          </InlineStack>
        </button>
      }
    >
      <ActionList
        actionRole="menuitem"
        sections={[
          { title: "Sort by", items: fieldItems },
          { title: "Order", items: orderItems },
        ]}
      />
    </Popover>
  );
}

function formatEventDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function EventRegistrationsPage() {
  const [query, setQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [eventFilter, setEventFilter] = useState("");
  const [eventOptions, setEventOptions] = useState([{ label: "All events", value: "" }]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [sortBy, setSortBy] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 1 });
  const [deletingId, setDeletingId] = useState(null);
  const fetchGenRef = useRef(0);

  const loadEventFilterOptions = useCallback(async () => {
    setEventsLoading(true);
    try {
      const body = await apiCall("event-registrations/events", "GET");
      const list = Array.isArray(body?.data) ? body.data : [];
      const options = [
        { label: "All events", value: "" },
        ...list.map((ev) => {
          const title = String(ev.event_title || "Untitled event").trim();
          const count = Number(ev.signup_count) || 0;
          return {
            label: `${title} (${count} signup${count === 1 ? "" : "s"})`,
            value: String(ev.event_id || ""),
          };
        }),
      ];
      setEventOptions(options);
    } catch (_e) {
      setEventOptions([{ label: "All events", value: "" }]);
    } finally {
      setEventsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEventFilterOptions();
  }, [loadEventFilterOptions]);

  useEffect(() => {
    const gen = ++fetchGenRef.current;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams({
          q: query,
          page: "1",
          limit: "50",
          sort: sortDir,
          sort_by: sortBy,
        });
        if (eventFilter) {
          params.set("event_id", eventFilter);
        }
        const body = await apiCall(`event-registrations?${params.toString()}`, "GET");
        if (cancelled || gen !== fetchGenRef.current) return;
        if (!body?.success) {
          throw new Error(body?.message || "Failed to load signups");
        }
        setRows(Array.isArray(body.data) ? body.data : []);
        setPagination(body.pagination || { page: 1, limit: 50, total: 0, totalPages: 1 });
      } catch (e) {
        if (cancelled || gen !== fetchGenRef.current) return;
        setError(e?.message || "Failed to load signups");
        setRows([]);
      } finally {
        if (!cancelled && gen === fetchGenRef.current) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [query, sortBy, sortDir, eventFilter]);

  useEffect(() => {
    const trimmed = searchInput.trim();

    if (trimmed.length === 0) {
      setQuery("");
      return undefined;
    }

    if (trimmed.length < MIN_SEARCH_LENGTH) {
      setQuery("");
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setQuery(trimmed);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const onSearch = useCallback(() => {
    const trimmed = searchInput.trim();
    if (trimmed.length > 0 && trimmed.length < MIN_SEARCH_LENGTH) return;
    setQuery(trimmed);
  }, [searchInput]);

  const searchButtonLoading =
    loading &&
    (query.length >= MIN_SEARCH_LENGTH || searchInput.trim().length >= MIN_SEARCH_LENGTH);

  const onDelete = useCallback(
    async (id) => {
      if (!id) return;
      setDeletingId(id);
      try {
        await apiCall(`event-registrations/${id}`, "DELETE");
        setRows((prev) => prev.filter((r) => r.id !== id));
        setPagination((p) => ({ ...p, total: Math.max(0, (p.total || 1) - 1) }));
        await loadEventFilterOptions();
      } catch (e) {
        setError(e?.message || "Delete failed");
      } finally {
        setDeletingId(null);
      }
    },
    [loadEventFilterOptions],
  );

  const sortDirOptions = useMemo(
    () => SORT_DIR_BY_FIELD[sortBy] || SORT_DIR_BY_FIELD.date,
    [sortBy],
  );

  useEffect(() => {
    const allowed = sortDirOptions.map((o) => o.value);
    if (!allowed.includes(sortDir)) {
      setSortDir(sortDirOptions[0].value);
    }
  }, [sortBy, sortDir, sortDirOptions]);

  const selectedEventLabel = useMemo(() => {
    if (!eventFilter) return null;
    return eventOptions.find((o) => o.value === eventFilter)?.label || null;
  }, [eventFilter, eventOptions]);

  const rangeLabel = useMemo(() => {
    const total = pagination.total || 0;
    if (!total) return "Showing 0 signups";
    const start = 1;
    const end = Math.min(rows.length, total);
    const eventPart = selectedEventLabel ? ` for ${selectedEventLabel.replace(/\s*\(\d+ signups?\)$/, "")}` : "";
    return `Showing ${start} – ${end} of ${total} signup${total === 1 ? "" : "s"}${eventPart}`;
  }, [pagination.total, rows.length, selectedEventLabel]);

  const emptyHeading = eventFilter || query ? "No signups match your filters" : "No signups yet";
  const emptyBody =
    eventFilter || query
      ? "Try another event or clear your search."
      : "When customers submit the event registration form on your storefront, entries appear here.";

  useAppTitleBar({ title: "Event signups" });

  return (
    <Page>
      <Card>
        <BlockStack gap="400">
          <InlineStack gap="300" wrap blockAlign="end">
            <div style={{ flex: "1 1 220px", minWidth: 200 }}>
              <Select
                label="Filter by event"
                options={eventOptions}
                value={eventFilter}
                onChange={setEventFilter}
                disabled={eventsLoading}
              />
            </div>
            <div
              style={{
                flex: "2 1 360px",
                minWidth: 280,
                display: "flex",
                alignItems: "flex-end",
                gap: 8,
              }}
            >
              <div style={{ flex: 1, minWidth: 160 }}>
                <TextField
                  label="Search with email"
                  labelHidden
                  placeholder="Search with email"
                  value={searchInput}
                  onChange={setSearchInput}
                  onClearButtonClick={() => {
                    setSearchInput("");
                    setQuery("");
                  }}
                  helpText={
                    searchInput.trim().length > 0 && searchInput.trim().length < MIN_SEARCH_LENGTH
                      ? `Type at least ${MIN_SEARCH_LENGTH} characters to search`
                      : undefined
                  }
                  prefix={<Icon source={SearchIcon} />}
                  autoComplete="off"
                  connectedRight={
                    <Button onClick={onSearch} loading={searchButtonLoading}>
                      Search
                    </Button>
                  }
                />
              </div>
              <SortByControl
                inline
                sortBy={sortBy}
                sortDir={sortDir}
                sortDirOptions={sortDirOptions}
                onSortByChange={setSortBy}
                onSortDirChange={setSortDir}
              />
            </div>
          </InlineStack>

          {!loading && error ? (
            <Text as="p" tone="critical">
              {error}
            </Text>
          ) : null}

          {loading ? (
            <PageLoader label="Loading signups" />
          ) : error ? null : (
            <>
              {rows.length === 0 ? (
                <Box padding="600">
                  <EmptyState heading={emptyHeading} image="">
                    <p>{emptyBody}</p>
                  </EmptyState>
                </Box>
              ) : (
                <>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "minmax(140px, 1.2fr) minmax(120px, 1fr) minmax(160px, 1.5fr) 40px",
                      gap: 12,
                      padding: "8px 12px",
                      borderBottom: "1px solid var(--p-color-border, #e3e3e3)",
                      fontWeight: 600,
                      fontSize: 13,
                    }}
                  >
                    <span>Event Title</span>
                    <span>Signed up</span>
                    <span>Contact</span>
                    <span />
                  </div>
                  {rows.map((row) => (
                <div
                  key={row.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "minmax(140px, 1.2fr) minmax(120px, 1fr) minmax(160px, 1.5fr) 40px",
                    gap: 12,
                    padding: "14px 12px",
                    borderBottom: "1px solid var(--p-color-border-secondary, #f1f1f1)",
                    alignItems: "start",
                  }}
                >
                  <BlockStack gap="100">
                    <Text as="p" variant="bodyMd" fontWeight="semibold">
                      {row.event_title || "Untitled event"}
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      Event: {formatEventDateTime(row.event_start_at) || "—"}
                    </Text>
                  </BlockStack>
                  <Text as="p" variant="bodySm">
                    {formatEventDateTime(row.created_at) || "—"}
                  </Text>
                  <BlockStack gap="050">
                    {row.registrant_name ? (
                      <Text as="p" variant="bodyMd">
                        {row.registrant_name}
                      </Text>
                    ) : null}
                    {row.registrant_email ? (
                      <Text as="p" variant="bodySm">
                        {row.registrant_email}
                      </Text>
                    ) : null}
                    {row.registrant_phone ? (
                      <Text as="p" variant="bodySm" tone="subdued">
                        {row.registrant_phone}
                      </Text>
                    ) : null}
                    <Text as="p" variant="bodySm" tone="subdued">
                      Guests: {Number(row.guest_count) || 0}
                    </Text>
                  </BlockStack>
                  <Button
                    variant="plain"
                    tone="critical"
                    icon={DeleteIcon}
                    accessibilityLabel="Delete signup"
                    loading={deletingId === row.id}
                    onClick={() => onDelete(row.id)}
                  />
                </div>
                  ))}
                </>
              )}
            </>
          )}

          <Text as="p" variant="bodySm" tone="subdued">
            {rangeLabel}
          </Text>
        </BlockStack>
      </Card>
    </Page>
  );
}
